import React from 'react';
import {
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Radio,
    RadioGroup,
    RadioProps,
} from '@carrotfertility/carotene-core';
import { Controller, UseControllerReturn, useFormContext, useFormState } from 'react-hook-form';
import { FormRadioGroupProps } from './form-types';
import { getFormInputState } from './form-utils';

interface RadioListProps
    extends Pick<FormRadioGroupProps, 'options' | 'name' | 'error' | 'renderItem'>,
        Pick<UseControllerReturn, 'field'> {}

function RadioList({ field, options, name, error, renderItem }: RadioListProps) {
    const isChecked = React.useCallback(
        (value: RadioProps['value']) => field.value && field.value === value,
        [field.value]
    );
    const showError = React.useCallback(
        (altColor: RadioProps['color']) => {
            return error ? 'error' : altColor;
        },
        [error]
    );
    const onChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            if (checked) {
                field.onChange(event.target.value);
            } else {
                field.onChange(field.value?.filter((value: string) => value !== event.target.value));
            }
        },
        [field]
    );

    return (
        <>
            {options.map(({ label, sx, ...optionProps }) =>
                renderItem ? (
                    renderItem({
                        formControlLabelProps: { label, sx },
                        radioProps: {
                            ...optionProps,
                            checked: isChecked(optionProps.value),
                            color: showError(optionProps.color),
                            onChange,
                            name,
                        },
                    })
                ) : (
                    <FormControlLabel
                        key={optionProps.value as string}
                        {...{ sx, label }}
                        control={
                            <Radio
                                {...optionProps}
                                {...{ onChange, name }}
                                checked={isChecked(optionProps.value)}
                                color={showError(optionProps.color)}
                            />
                        }
                    />
                )
            )}
        </>
    );
}

export function FormRadioGroup({
    name,
    label,
    controllerProps,
    helperText,
    error,
    options,
    renderItem,
    ...otherProps
}: FormRadioGroupProps): JSX.Element {
    const { errors, isSubmitting } = useFormState();
    const { control } = useFormContext();

    const { error: hasError, helperText: helperTextValue } = getFormInputState({ name, errors, error, helperText });

    return (
        <FormControl
            component="fieldset"
            required={otherProps.required ? otherProps.required : Boolean(controllerProps?.rules?.required)}
            error={hasError}
            {...otherProps}
            disabled={isSubmitting || otherProps.disabled}
        >
            {label ? <FormLabel component="legend">{label}</FormLabel> : null}
            <RadioGroup>
                <Controller
                    {...(controllerProps || {})}
                    {...{ control, name }}
                    defaultValue={controllerProps?.defaultValue || ''}
                    render={({ field }) => <RadioList {...{ field, options, name, error: hasError, renderItem }} />}
                />
            </RadioGroup>
            <FormHelperText>{helperTextValue}</FormHelperText>
        </FormControl>
    );
}
